import { Overlay } from '@angular/cdk/overlay';
import { isPlatformBrowser, NgClass, NgFor, NgIf, NgTemplateOutlet } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, ElementRef, EventEmitter, HostBinding, Inject, inject, Input, OnChanges, OnDestroy, OnInit, Output, PLATFORM_ID, Renderer2, SimpleChanges, ViewChild, ViewEncapsulation, ChangeDetectorRef } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { MAT_AUTOCOMPLETE_SCROLL_STRATEGY, MatAutocomplete, MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { RouterLink } from '@angular/router';
import { fuseAnimations } from '@fuse/animations/public-api';
import { TFMService } from 'app/mock-api/tfm.service';
import { debounceTime, filter, map, Subject, takeUntil } from 'rxjs';

@Component({
    selector     : 'search',
    templateUrl  : './search.component.html',
    encapsulation: ViewEncapsulation.None,
    exportAs     : 'fuseSearch',
    animations   : fuseAnimations,
    standalone   : true,
    imports      : [NgIf, MatButtonModule, MatIconModule, FormsModule, MatAutocompleteModule, ReactiveFormsModule, MatOptionModule, NgFor, RouterLink, NgTemplateOutlet, MatFormFieldModule, MatInputModule, NgClass],
    providers    : [
        {
            provide   : MAT_AUTOCOMPLETE_SCROLL_STRATEGY,
            useFactory: () =>
            {
                const overlay = inject(Overlay);
                return () => overlay.scrollStrategies.block();
            },
        },
    ],
})
export class SearchComponent implements OnChanges, OnInit, OnDestroy
{
    @Input() appearance: 'basic' | 'bar' = 'basic';
    @Input() debounce: number = 300;
    @Input() minLength: number = 2;
    @Output() search: EventEmitter<any> = new EventEmitter<any>();

    public view : any = {};
    
    opened: boolean = false;
    resultSets: any[];
    searchControl: UntypedFormControl = new UntypedFormControl();
    private _matAutocomplete: MatAutocomplete;
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    /**
     * Constructor
     */
    constructor(
        private _elementRef: ElementRef,
        private _httpClient: HttpClient,
        private _renderer2: Renderer2,
        private _tfmService: TFMService,
        private _cd: ChangeDetectorRef,
        @Inject(PLATFORM_ID) private platformId
    )
    {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Host binding for component classes
     */
    @HostBinding('class') get classList(): any
    {
        return {
            'search-appearance-bar'  : this.appearance === 'bar',
            'search-appearance-basic': this.appearance === 'basic',
            'search-opened'          : this.opened,
        };
    }

    /**
     * Setter for bar search input
     *
     * @param value
     */
    @ViewChild('barSearchInput')
    set barSearchInput(value: ElementRef)
    {
        // If the value exists, it means that the search input
        // is now in the DOM, and we can focus on the input..
        if ( value )
        {
            // Give Angular time to complete the change detection cycle
            setTimeout(() =>
            {
                // Focus to the input element
                value.nativeElement.focus();
            });
        }
    }

    /**
     * Setter for mat-autocomplete element reference
     *
     * @param value
     */
    @ViewChild('matAutocomplete')
    set matAutocomplete(value: MatAutocomplete)
    {
        this._matAutocomplete = value;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On changes
     *
     * @param changes
     */
    ngOnChanges(changes: SimpleChanges): void
    {
        this._cd.detectChanges();
        // Appearance
        if ( 'appearance' in changes )
        {
            // To prevent any issues, close the
            // search after changing the appearance
            this.close();
        }
    }

    /**
     * On init
     */
    ngOnInit(): void {
        if (isPlatformBrowser(this.platformId)) {
            if (this.hasSelectedChannel()) {
                this._tfmService.getSite().then(res => {
                    this.view.site = res;
                });
            } else {
                this.view.site = { name: 'No Channel' };
            }

            // Subscribe to the search field value changes
            this.searchControl.valueChanges
                .pipe(
                    debounceTime(this.debounce),
                    takeUntil(this._unsubscribeAll),
                    map((value) => {
                        // Set the resultSets to null if there is no value or
                        // the length of the value is smaller than the minLength
                        // so the autocomplete panel can be closed
                        if (!value || value.length < this.minLength) {
                            this.resultSets = null;
                        }

                        // Continue
                        return value;
                    }),
                    // Filter out undefined/null/false statements and also
                    // filter out the values that are smaller than minLength
                    filter(value => value && value.length >= this.minLength),
                )
                .subscribe((value) => {
                    // this._httpClient.post('api/common/search', {query: value})
                    //     .subscribe((resultSets: any) =>
                    //     {
                    //         // Store the result sets
                    //         this.resultSets = resultSets;

                    //         // Execute the event
                    //         this.search.next(resultSets);
                    //     });
                    this._tfmService.getLoggedInUser().subscribe(res => {
                        var channels = [];
                        var user = res;

                        let agenciesProcessed = 0;
                        this._tfmService.getAgencies().subscribe(agencies => {
                            agencies.content.forEach(agency => {

                                agenciesProcessed++;

                                var agencySites = [];
                                const isWebTravelBrands = user.agencyId == 8;
                                const isXLGroup = agency.id == 23;

                                if (user.siteID > 0) {
                                    if (isWebTravelBrands && isXLGroup) {
                                        agencySites = agency.sites;
                                    } else {
                                        agency.sites.forEach(site => {
                                            if (site.id == user.siteID) {
                                                agencySites.push(site);
                                            }
                                        });
                                    }
                                } else {
                                    agencySites = agency.sites;
                                }

                                agency.sites = agencySites;

                                if (user.agencyId == agency.id || user.agentType == "ADMIN" || (isWebTravelBrands && isXLGroup)) {
                                    channels.push(agency);

                                    setTimeout(() => {
                                        if (agenciesProcessed == agencies.content.length) {
                                            //this.channelsLoaded = true;

                                            var localAgencySite = this._tfmService.getLocalAgencySite();

                                            //   this.selectedChannel = user.siteID > 0 ? user.siteID : localAgencySite.siteID;
                                            if (user.agencyId == agency.id) {
                                                //this.channelChange();
                                                if (agency.sites.filter(x => x.id == user.siteID).length > 0) {
                                                    this.setChannel(agency.sites.filter(x => x.id == user.siteID)[0]);
                                                }
                                            }
                                        }
                                    }, 500);

                                }
                            });

                            var resultSet = [];

                            channels.forEach(a => {
                                if (a.sites.filter(x => x.name.toLowerCase().includes(value.toLowerCase())).length > 0) {
                                    resultSet.push({
                                        id: 'channels',
                                        label: `${a.name}`,
                                        results: a.sites.filter(x => x.name.toLowerCase().includes(value.toLowerCase()))
                                    });
                                }
                            });
                            this.resultSets = resultSet;
                            console.log(resultSet);

                            this.search.next(channels);

                        });
                    });
                });
        }
    }

    private hasSelectedChannel(): boolean {
        const siteId = Number(localStorage.getItem('siteID') ?? 0);
        if (siteId > 0) {
            return true;
        }

        const siteGuid = (localStorage.getItem('siteGUID') || '').trim();
        return siteGuid.length > 0;
    }

    public setChannel(channel) {
        var site = this._tfmService.getLocalAgencySite();

        //this._tfmService.setLocalAgencySite(agency.id, site.id);
        localStorage.setItem("agencyID", channel.agencyID);
        localStorage.setItem("siteID", channel.id);
        localStorage.setItem("siteGUID", channel.guid);
        localStorage.setItem("siteName", channel.name);

        // console.log(channel);
        this.view.site = channel;

        if (!site || (site.siteID != channel.id || site.agencyID != site.agencyID)) {
            window.location.reload();   
        }
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void
    {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * On keydown of the search input
     *
     * @param event
     */
    onKeydown(event: KeyboardEvent): void
    {
        // Escape
        if ( event.code === 'Escape' )
        {
            // If the appearance is 'bar' and the mat-autocomplete is not open, close the search
            if ( this.appearance === 'bar' && !this._matAutocomplete.isOpen )
            {
                this.close();
            }
        }
    }

    /**
     * Open the search
     * Used in 'bar'
     */
    open(): void
    {
        // Return if it's already opened
        if ( this.opened )
        {
            return;
        }

        // Open the search
        this.opened = true;
    }

    /**
     * Close the search
     * * Used in 'bar'
     */
    close(): void
    {
        // Return if it's already closed
        if ( !this.opened )
        {
            return;
        }

        // Clear the search input
        this.searchControl.setValue('');

        // Close the search
        this.opened = false;
    }

    /**
     * Track by function for ngFor loops
     *
     * @param index
     * @param item
     */
    trackByFn(index: number, item: any): any
    {
        return item.id || index;
    }
}
